import React, { useContext, useState } from 'react';
import { Box, Chip, Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SupplierLocationCardBadge from './SupplierLocationCardBadge';
import LocationFeatureList from '../../location/search/LocationFeatureList';
import LocationItemPriceDisplay from '../../location/LocationItemPriceDisplay';
import LocationFavoriteIcon from '../../location/LocationFavoriteIcon';
import ImageNotAvailablePlaceholder from '../../location/ImageNotAvailablePlaceholder';
import { constructListingImageUrl } from '../../../util/ConstructImageUrlUtil';
import { AppContext } from '../../../context/app-context';
import { isBuyerAccountType } from '../../../util/AccountUtils';
import type { PublicLocationVO } from '../../../types/model/valueObject/account/PublicLocationVO';

// Feature labels used to build short chip labels
const FEATURE_CHIP_MAX = 3;

interface SupplierLocationCardProps {
  location: PublicLocationVO;
  variant?: 'grid' | 'row';
}

const SupplierLocationCard = ({ location, variant = 'row' }: SupplierLocationCardProps) => {
  const appContext = useContext(AppContext);
  const currentLocation = useLocation();
  const isAuthenticated = !!appContext?.user;
  const isBuyer = isBuyerAccountType(appContext?.user?.type);
  const [imgError, setImgError] = useState(false);

  const favoriteControl = isBuyer ? (
    <LocationFavoriteIcon locationId={location.locationId} />
  ) : !isAuthenticated ? (
    <IconButton
      color="primary"
      aria-label="Log in to save this location"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(
          `/login?returnUrl=${encodeURIComponent(currentLocation.pathname)}`
        );
      }}
    >
      <FavoriteBorderIcon />
    </IconButton>
  ) : null;

  // ── Grid variant ────────────────────────────────────────────────────────────
  if (variant === 'grid') {
    const visibleFeatures = location.features?.slice(0, FEATURE_CHIP_MAX) ?? [];
    const extraCount = (location.features?.length ?? 0) - FEATURE_CHIP_MAX;

    const hasPrice = (location.pricePerMonth ?? 0) > 0 || (location.pricePerDay ?? 0) > 0;

    return (
      <Box
        sx={{
          border: '1px solid #E9E9FB',
          borderRadius: 2,
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            borderColor: 'primary.main',
          },
        }}
      >
        <Link
          to={`/location-profile/${location.locationId}`}
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', flex: 1 }}
        >
          {/* Image box — 62% padding-top = ~16:10 aspect ratio */}
          <Box sx={{ position: 'relative', paddingTop: '62%', overflow: 'hidden', bgcolor: '#f4f4f8' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                '& img': { width: '100%', height: '100%', objectFit: 'cover' },
              }}
            >
              {location.listingImageFileName && !imgError ? (
                <img
                  src={constructListingImageUrl(location.listingImageFileName)}
                  alt={location.locationName}
                  onError={() => setImgError(true)}
                />
              ) : (
                <ImageNotAvailablePlaceholder height="100%" width="100%" borderRadius="0" />
              )}
            </Box>

            <SupplierLocationCardBadge hasInstantApproval={location.hasInstantApproval} />
          </Box>

          {/* Card body — outer wrapper has no padding so divider spans full width */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Content section */}
            <Box sx={{ p: '0.75rem 1rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    fontFamily: 'Satoshi-Variable, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    lineHeight: 1.3,
                    color: 'text.primary',
                  }}
                >
                  {location.locationName}
                </Typography>
                {favoriteControl && (
                  <Box onClick={(e) => e.preventDefault()} sx={{ flexShrink: 0, ml: 0.5 }}>
                    {favoriteControl}
                  </Box>
                )}
              </Box>

              {/* City + state with pin icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                <PlaceOutlinedIcon sx={{ fontSize: '0.85rem', color: 'text.secondary', flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.82rem',
                    color: 'text.secondary',
                  }}
                >
                  {location.city}, {location.state}
                </Typography>
              </Box>

              {/* Feature chips */}
              {visibleFeatures.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {visibleFeatures.map((f) => (
                    <Chip
                      key={f}
                      label={f}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', height: 22 }}
                    />
                  ))}
                  {extraCount > 0 && (
                    <Chip
                      label={`+${extraCount} more`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', height: 22 }}
                    />
                  )}
                </Stack>
              )}
            </Box>

            {/* Divider + footer pinned below content */}
            <Divider sx={{ mx: '1rem', borderColor: 'rgba(0,0,0,0.07)' }} />
            <Box sx={{ px: '1rem', py: '0.6rem' }}>
              {hasPrice ? (
                <LocationItemPriceDisplay
                  pricePerMonth={location.pricePerMonth ?? 0}
                  pricePerDay={location.pricePerDay ?? 0}
                />
              ) : (
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: 'text.secondary' }}>
                  Contact for pricing
                </Typography>
              )}
            </Box>
          </Box>
        </Link>
      </Box>
    );
  }

  // ── Row variant (original layout) ───────────────────────────────────────────
  return (
    <Box
      sx={{
        border: '1px solid #E9E9FB',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <Link
        to={`/location-profile/${location.locationId}`}
        style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'row', flex: 1 }}
      >
        <Box
          sx={{
            width: 200,
            minWidth: 200,
            minHeight: 160,
            overflow: 'hidden',
            '& img': { width: '100%', height: '100%', objectFit: 'cover' },
          }}
        >
          {location.listingImageFileName && !imgError ? (
            <img
              src={constructListingImageUrl(location.listingImageFileName)}
              alt={location.locationName}
              onError={() => setImgError(true)}
            />
          ) : (
            <ImageNotAvailablePlaceholder height="100%" width="100%" borderRadius="0" />
          )}
        </Box>
        <Box sx={{ p: '0.75rem 1rem 1rem', flex: 1, minWidth: 0 }}>
          <Grid container justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
            <Grid item xs>
              <Typography sx={{ fontFamily: 'Satoshi-Variable, sans-serif', fontWeight: 600, fontSize: '1rem', lineHeight: 1.3, color: 'text.primary' }}>
                {location.locationName}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: 'text.secondary', mt: 0.25 }}>
                {location.city}, {location.state}
              </Typography>
            </Grid>
            <Grid item onClick={(e) => e.preventDefault()}>
              {favoriteControl}
            </Grid>
          </Grid>
          <Grid item mt={0.5}>
            <LocationFeatureList maxFeatures={4} features={location.features} />
          </Grid>
          <Grid item mt={0.5}>
            <LocationItemPriceDisplay
              pricePerMonth={location.pricePerMonth || 0}
              pricePerDay={location.pricePerDay || 0}
            />
          </Grid>
        </Box>
      </Link>
    </Box>
  );
};

export default SupplierLocationCard;
